import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { useAsyncStatus } from '../../../hooks';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { GetLicensesResponse } from '../../../../typings/api';
import type { AsyncStatus } from '../../../types';

type UseLicenses = {
  status: AsyncStatus;
  licenses: GetLicensesResponse;
};

/**
 * Hook for fetching licenses based on the search query.
 *
 * @param {string} searchQuery - Given search query.
 */
const useLicenses = (searchQuery: string): UseLicenses => {
  const { status, ...methods } = useAsyncStatus();
  const [licenses, setLicenses] = useState<GetLicensesResponse>([]);

  //  Fetch licenses.
  async function searchLicenses(query: string): Promise<void> {
    if (query.trim().length > 0) {
      try {
        methods.onPending();
        const { data } = await get<GetLicensesResponse>(`/api/licenses?$title=${nuc(query)}`);
        methods.onSuccess();
        setLicenses(data as GetLicensesResponse || []);
      } catch (error) {
        methods.onError(error);
      }
    }
  }

  const debouncedFetch = useCallback(_.debounce(searchLicenses, 250), []);

  useEffect(() => {
    debouncedFetch(searchQuery);
  }, [searchQuery]);

  return {
    status,
    licenses: searchQuery.trim().length > 0
      ? licenses
      : [],
  };
};

export default useLicenses;
